import Icon from '@components/v2/ui/Icon/Icon';
import IconButton from '@components/v2/ui/IconButton/IconButton';
import Loading from '@components/v2/ui/Loading/Loading';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box } from '@mui/material';
import { X } from 'lucide-react';
import LangEnum from '../../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../translation/TranslationContext';
import {
  formatRachelFileSize,
  getRachelFileExtensionLabel,
  getRachelFileIcon,
  splitRachelFileName,
} from '../../../../rachel.files.presentation';
import { RachelAttachment } from '../../../../rachel.files.types';
import { useRachelAttachmentCardStyles } from './rachelAttachmentCard.styles';

const META_SEPARATOR = '·';

const PROGRESS_SIZE = 16;

interface RachelAttachmentCardProps {
  attachment: RachelAttachment;
  onRemove: (id: string) => void;
}

const RachelAttachmentCard = ({ attachment, onRemove }: RachelAttachmentCardProps) => {
  const { translate } = useTranslation();
  const { classes } = useRachelAttachmentCardStyles();

  const { file, status } = attachment;
  const { base, extension } = splitRachelFileName(file.name);
  const extensionLabel = getRachelFileExtensionLabel(file.type, file.name);
  const sizeLabel = formatRachelFileSize(file.size);
  const metaLabel = extensionLabel ? `${sizeLabel} ${META_SEPARATOR} ${extensionLabel}` : sizeLabel;

  return (
    <Box test-id="rachel-attachment-card" className={classes.card}>
      <Box className={classes.info}>
        <Box className={classes.badge}>
          {status === 'uploading' ? (
            <Loading size={PROGRESS_SIZE} />
          ) : (
            <Icon icon={getRachelFileIcon(file.type)} size="lg" />
          )}
        </Box>
        <Box className={classes.content}>
          <Box className={classes.nameRow}>
            <Typography noWrap variant="micro1" title={file.name} className={classes.nameBase}>
              {base}
            </Typography>
            {extension && (
              <Typography variant="micro1" className={classes.nameExtension}>
                {extension}
              </Typography>
            )}
          </Box>
          <Typography noWrap variant="micro2" color="text.secondary">
            {metaLabel}
          </Typography>
        </Box>
      </Box>
      <IconButton
        icon={X}
        size="small"
        className={classes.removeButton}
        ariaLabel={translate(LangEnum.RACHEL_ATTACHMENT_REMOVE)}
        onClick={() => onRemove(attachment.id)}
      />
    </Box>
  );
};

export default RachelAttachmentCard;
